'use client';

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionTitle from '@/components/ui/SectionTitle';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { SlidersHorizontal, ShieldAlert } from 'lucide-react';
import { useCountry } from '@/context/CountryContext';
import { useLanguage } from '@/context/LanguageContext';
import { connectionsByRegion } from '@/lib/mock-data';
import { computeReconciliation, getHeldContractors } from '@/lib/reconciliation';
import type { ReconRow } from '@/lib/reconciliation';
import type { ReconStatus } from '@/lib/reconciliation-config';
import { defaultRatios, defaultRatioMap, defaultBlendWeights, defaultCommissioningLagDays,  } from '@/lib/reconciliation-config';
import ReconDrawer from './components/ReconDrawer';

// ─── useCountUp hook ───────────────────────────────────────────────────────────
function useCountUp(target: number, duration = 900): number {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const startValueRef = useRef(0);

  const animate = useCallback(
    (timestamp: number) => {
      if (startRef.current === null) {
        startRef.current = timestamp;
        startValueRef.current = 0;
      }
      const elapsed = timestamp - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out: 1 - (1 - t)^3
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(startValueRef.current + (target - startValueRef.current) * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setValue(target);
      }
    },
    [target, duration]
  );

  useEffect(() => {
    // Check prefers-reduced-motion
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      setValue(target);
      return;
    }

    startRef.current = null;
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, animate]);

  return value;
}

// ─── Confidence Meter ─────────────────────────────────────────────────────────
function ConfidenceMeter({ level }: { level: 'high' | 'medium' | 'low' }) {
  const filled = level === 'high' ? 3 : level === 'medium' ? 2 : 1;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="w-2 h-3 rounded-sm"
          style={{
            backgroundColor: i <= filled ? 'var(--primary)' : 'var(--border)',
          }}
        />
      ))}
    </div>
  );
}

// ─── Status colour helper ──────────────────────────────────────────────────────
function varianceColor(status: ReconStatus): string {
  if (status === 'on_track') return 'var(--status-green)';
  if (status === 'investigate') return 'var(--status-red)';
  if (status === 'over_reported') return 'var(--geo)';
  return 'var(--status-amber)';
}

// ─── Hero Counter Card ────────────────────────────────────────────────────────
function HeroCard({
  label,
  value,
  suffix = '',
  color,
}: {
  label: string;
  value: number;
  suffix?: string;
  color?: string;
}) {
  const { fmtNum } = useLanguage();
  const animated = useCountUp(value);
  return (
    <div className="bg-surface-card rounded-xl border border-surface-border p-5 flex flex-col gap-1">
      <p className="text-xs font-medium text-text-muted">{label}</p>
      <p
        className="text-2xl font-bold tabular-nums tracking-tight"
        style={{ color: color ?? 'var(--foreground)' }}
      >
        {fmtNum(animated)}{suffix}
      </p>
    </div>
  );
}

export default function ReconciliationPage() {
  const { requisitions, code } = useCountry();
  const { t, fmtNum } = useLanguage();

  // Local state for adjustable ratios
  const [ratios, setRatios] = useState<Record<string, number>>(() => ({ ...defaultRatioMap }));
  const [lagDays, setLagDays] = useState(defaultCommissioningLagDays);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ReconRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Recompute on country change — reset ratios
  useEffect(() => {
    setRatios({ ...defaultRatioMap });
    setLagDays(defaultCommissioningLagDays);
  }, [code]);

  const result = useMemo(
    () =>
      computeReconciliation({
        requisitions,
        regional: connectionsByRegion,
        ratios,
        weights: defaultBlendWeights,
        lagDays,
      }),
    [requisitions, ratios, lagDays]
  );

  const { rows, totals } = result;
  const heldContractors = useMemo(() => getHeldContractors(rows), [rows]);

  // Chart data
  const chartData = useMemo(
    () =>
      rows.map((r) => ({
        name: r.contractor.length > 18 ? r.contractor.slice(0, 16) + '…' : r.contractor,
        expected: r.expectedConnections,
        observed: r.observedReported,
      })),
    [rows]
  );

  const netVarianceStatus: ReconStatus =
    totals.netVariancePct >= -15 && totals.netVariancePct <= 15
      ? 'on_track'
      : totals.netVariancePct < -40
      ? 'investigate'
      : totals.netVariancePct > 25
      ? 'over_reported' :'watch';

  const handleRowClick = (row: ReconRow) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  };

  const handleReset = () => {
    setRatios({ ...defaultRatioMap });
    setLagDays(defaultCommissioningLagDays);
  };

  return (
    <AppLayout title={t('page.reconTitle')} subtitle={t('page.reconSub')}>
      {/* Hero Counters */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <HeroCard label={t('recon.materialsReleased')} value={totals.totalReleasedUnits} />
        <HeroCard label={t('recon.expected')} value={totals.totalExpected} />
        <HeroCard label={t('recon.observed')} value={totals.totalObservedReported} />
        <HeroCard
          label={t('recon.netVariance')}
          value={Math.abs(totals.netVariancePct)}
          suffix={`%`}
          color={varianceColor(netVarianceStatus)}
        />
      </div>

      {/* Flag Strip */}
      <div className="mb-6">
        {heldContractors.length === 0 ? (
          <p className="text-sm text-text-muted px-1">{t('recon.noFlags')}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {rows
              .filter((r) => r.status === 'investigate')
              .map((r) => (
                <div
                  key={r.contractor}
                  className="flex items-center gap-4 rounded-xl px-4 py-3 border"
                  style={{
                    backgroundColor: 'rgba(220,38,38,0.06)',
                    borderColor: 'rgba(220,38,38,0.2)',
                  }}
                >
                  <ShieldAlert size={16} style={{ color: 'var(--status-red)' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-status-red truncate">{r.contractor}</p>
                    <p className="text-xs text-text-muted">{r.region}</p>
                  </div>
                  <span className="text-sm font-bold tabular-nums text-status-red">
                    {r.variancePct > 0 ? '+' : ''}{r.variancePct}%
                  </span>
                  <p className="text-xs font-medium text-status-red hidden sm:block">
                    {t('recon.holdApplied')}
                  </p>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Model Controls */}
      <div className="bg-surface-card rounded-xl border border-surface-border mb-6 overflow-hidden">
        <button
          onClick={() => setPanelOpen((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-surface-light transition-colors"
        >
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={16} style={{ color: 'var(--primary)' }} />
            <span className="text-sm font-semibold text-text-main">{t('recon.modelControls')}</span>
          </div>
          <span className="text-xs text-text-muted">{panelOpen ? '▲' : '▼'}</span>
        </button>

        {panelOpen && (
          <div className="px-5 pb-5 border-t border-surface-border">
            {/* Ratio inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {defaultRatios.map((entry) => (
                <div key={entry.itemCode} className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-text-muted">
                    {t(`material.${entry.itemCode}`) !== `material.${entry.itemCode}`
                      ? t(`material.${entry.itemCode}`)
                      : entry.itemCode}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      step={entry.connectionsPerUnit >= 1 ? 1 : 0.001}
                      value={ratios[entry.itemCode] ?? entry.connectionsPerUnit}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v >= 0) {
                          setRatios((prev) => ({ ...prev, [entry.itemCode]: v }));
                        }
                      }}
                      className="w-full border border-surface-border rounded-lg px-2.5 py-1.5 text-sm text-text-main bg-surface-light focus:outline-none focus:ring-2 focus:ring-accent/30"
                    />
                    <span className="text-[10px] text-text-muted whitespace-nowrap flex-shrink-0">
                      {t('recon.perUnit')}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Lag slider */}
            <div className="mt-5">
              <label className="text-xs font-medium text-text-muted block mb-2">
                {t('recon.commissioningLag')}:{' '}
                <span className="font-bold text-text-main">{lagDays} {t('recon.days')}</span>
              </label>
              <input
                type="range"
                min={0}
                max={180}
                step={15}
                value={lagDays}
                onChange={(e) => setLagDays(parseInt(e.target.value, 10))}
                className="w-full accent-primary"
                style={{ accentColor: 'var(--primary)' }}
              />
              <div className="flex justify-between text-[10px] text-text-muted mt-1">
                <span>0</span>
                <span>90</span>
                <span>180</span>
              </div>
            </div>

            {/* Reset */}
            <button
              onClick={handleReset}
              className="mt-4 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors hover:bg-surface-light"
              style={{ borderColor: 'var(--border)', color: 'var(--primary)' }}
            >
              {t('recon.resetDefaults')}
            </button>
          </div>
        )}
      </div>

      {/* Comparison Chart */}
      <div className="bg-surface-card rounded-xl border border-surface-border p-5 mb-6">
        <SectionTitle
          title={t('recon.expected') + ' vs ' + t('recon.observed')}
          subtitle={t('recon.connections')}
        />
        <div className="h-[300px] w-full mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                interval={0}
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                label={{
                  value: t('recon.connections'),
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 10, fill: 'var(--text-muted)' },
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Bar
                dataKey="expected"
                name={t('recon.expected')}
                fill="var(--muted)"
                isAnimationActive={true}
                animationDuration={500}
              />
              <Bar
                dataKey="observed"
                name={t('recon.observed')}
                fill="var(--primary)"
                isAnimationActive={true}
                animationDuration={500}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Reconciliation Table */}
      <div className="bg-surface-card rounded-xl border border-surface-border">
        <div className="p-5 border-b border-surface-border">
          <SectionTitle
            title={t('page.reconTitle')}
            subtitle={`${rows.length} contractors`}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {[
                  t('table.contractor'),
                  t('table.region'),
                  t('recon.releasedUnits'),
                  t('recon.expected'),
                  t('recon.observed'),
                  t('recon.verified'),
                  t('recon.variance'),
                  t('recon.confidence'),
                  t('table.status'),
                ].map((col) => (
                  <th
                    key={col}
                    className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-text-muted"
                    style={{ backgroundColor: 'var(--background)', letterSpacing: '0.06em' }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-16 text-center text-sm text-text-muted">
                    {t('common.noResults')}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr
                    key={row.contractor}
                    className="cursor-pointer transition-colors"
                    style={{
                      borderBottom: idx < rows.length - 1 ? '1px solid var(--border)' : 'none',
                    }}
                    onClick={() => handleRowClick(row)}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                        'var(--background)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '';
                    }}
                  >
                    <td className="py-3 px-4 font-medium text-text-main text-xs max-w-[160px]">
                      <span className="truncate block">{row.contractor}</span>
                    </td>
                    <td className="py-3 px-4 text-xs text-text-muted whitespace-nowrap">
                      {row.region}
                    </td>
                    <td className="py-3 px-4 text-xs tabular-nums text-text-main">
                      {fmtNum(row.releasedUnits)}
                    </td>
                    <td className="py-3 px-4 text-xs tabular-nums text-text-main">
                      {fmtNum(row.expectedConnections)}
                    </td>
                    <td className="py-3 px-4 text-xs tabular-nums text-text-main">
                      {fmtNum(row.observedReported)}
                    </td>
                    <td className="py-3 px-4 text-xs tabular-nums text-text-muted">
                      {fmtNum(row.observedVerified)}
                    </td>
                    <td className="py-3 px-4 text-xs tabular-nums font-semibold whitespace-nowrap"
                      style={{ color: varianceColor(row.status) }}>
                      {row.variancePct > 0 ? '+' : ''}{row.variancePct}%
                    </td>
                    <td className="py-3 px-4">
                      <ConfidenceMeter level={row.confidence} />
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge
                        status={row.status as ReconStatus}
                        label={t(`status.${row.status}`)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer */}
      <ReconDrawer
        row={selectedRow}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        weights={defaultBlendWeights}
        lagDays={lagDays}
      />
    </AppLayout>
  );
}
